'use client';

import { useRouter, usePathname } from 'next/navigation';
import dynamic from 'next/dynamic';
import { Suspense } from 'react';

import {
  useAuthorProfileArticleList,
  useAuthorProfileInfo,
  useAuthorSubscription,
} from '@/entities/author';
import { useProfile, useTimedOpen } from '@/shared/hooks';
import { PATH } from '@/shared/constants';
import { Skeleton } from '@/shared/Components';

const ArticleAuthorInfoClient = dynamic(
  () => import('@/features/ArticleAuthorInfo'),
  { ssr: false }
);

interface ArticleAuthorProfileProps {
  authorId: number;
  articleId: string;
}

const ArticleAuthorProfile = ({
  authorId,
  articleId,
}: ArticleAuthorProfileProps) => {
  const router = useRouter();
  const pathname = usePathname();
  const { profile } = useProfile();
  const { data: articles } = useAuthorProfileArticleList({
    authorId,
    articleId: Number(articleId),
  });
  const { data: authorInfo } = useAuthorProfileInfo({ authorId });
  const { mutate } = useAuthorSubscription();
  const [isTooltipOpen, openTooltip] = useTimedOpen();

  const subscribe = () => {
    if (!profile) return router.push(`${PATH.AUTH_LOGIN}?next=${pathname}`);
    mutate(authorId, { onSuccess: () => openTooltip() });
  };

  if (!articles) return <Skeleton className="h-[280px]" />;
  if (!authorInfo) return <Skeleton className="h-[280px]" />;

  const hasMoreThanThreeArticles = articles.length > 3;
  const topThreeArticles = articles.slice(0, 3);

  return (
    <Suspense fallback={<Skeleton className="h-[280px]" />}>
      <ArticleAuthorInfoClient
        articles={topThreeArticles}
        authorInfo={authorInfo}
        hasMore={hasMoreThanThreeArticles}
        isTooltipOpen={isTooltipOpen}
        onSubscribe={subscribe}
      />
    </Suspense>
  );
};

export default ArticleAuthorProfile;
